import type { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useOrgSlug } from '@/hooks/useOrgSlug';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { usePublicOrgProfile } from '@/queries/publicOrgProfile';
import { useOrgHost } from '@/contexts/OrgHostContext';
import { hasAnyRole, hasRole, TierFeatureKey, normalizeSubscriptionTier } from '@court-sync/shared';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { useAcademyFeatureAccess } from '@/hooks/useFeatureAccess';
import { StarterPortalUnavailable } from '@/components/guardian/StarterPortalUnavailable';
import { PublicOrgRouteShell } from '@/components/public/PublicOrgRouteShell';

interface PublicOrgMemberGateProps {
  children?: ReactNode;
}

/**
 * Access gate for authenticated consumer routes inside the public org shell (no dashboard sidebar).
 */
export function PublicOrgMemberGate({ children }: PublicOrgMemberGateProps) {
  const orgSlug = useOrgSlug();
  const { host, academy: hostAcademy, loading: hostLoading } = useOrgHost();
  const { profile, isLoading: profileLoading } = usePublicOrgProfile({
    slug: orgSlug,
    subdomainLabel: host.mode === 'subdomain' ? host.subdomainLabel : undefined,
    hostLoading: host.mode === 'subdomain' && hostLoading,
    enabled: !!orgSlug || (host.mode === 'subdomain' && !!host.subdomainLabel),
  });
  const { user } = useAuthStore();
  const { academy } = useGuardianAcademyStore();
  const { p } = useOrgPaths(profile ? { slug: profile.organization.slug } : null);

  const resolvedAcademy = academy ?? hostAcademy;
  const { hasAccess: hasPortalAccess } = useAcademyFeatureAccess(
    resolvedAcademy,
    TierFeatureKey.STUDENT_GUARDIAN_PORTAL
  );

  if (!user || !hasAnyRole(user, ['guardian', 'student', 'member'])) {
    const loginPath = p?.('/login') ?? '/login';
    return <Navigate to={loginPath} replace />;
  }

  if (!orgSlug) {
    return <Navigate to="/org-picker" replace />;
  }

  if (profileLoading || (host.mode === 'subdomain' && hostLoading)) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="mx-auto max-w-lg px-4 py-16 text-center">
        <h1 className="text-xl font-semibold">Portal unavailable</h1>
        <p className="mt-2 text-muted-foreground">Organization not found</p>
      </div>
    );
  }

  const userHasLessonsRole = hasRole(user, 'guardian') || hasRole(user, 'student');
  const hasLessonsSection = Boolean(
    userHasLessonsRole && profile.modules.hasAcademy && resolvedAcademy
  );
  const hasClubPortal = Boolean(
    profile.modules.hasClub &&
    hasRole(user, 'member') &&
    (profile.modules.membershipsEntitled || profile.modules.courtHireEntitled)
  );

  if (!hasLessonsSection && !hasClubPortal) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4 text-center text-muted-foreground">
        This organization portal is not available.
      </div>
    );
  }

  if (
    hasLessonsSection &&
    resolvedAcademy &&
    !hasPortalAccess &&
    normalizeSubscriptionTier(resolvedAcademy.subscriptionTier) === 'starter' &&
    hasAnyRole(user, ['guardian', 'student'])
  ) {
    return (
      <PublicOrgRouteShell>
        <div className="mx-auto max-w-3xl px-4 py-10">
          <StarterPortalUnavailable
            academy={resolvedAcademy}
            portalLabel="Student & guardian portal"
            publicLessonsPath={p?.('/lessons') ?? `/club-academy/${orgSlug}`}
          />
        </div>
      </PublicOrgRouteShell>
    );
  }

  return children ?? <Outlet />;
}
